import { VError, WError } from 'verror';
import moment from 'moment';
import _ from 'lodash';
import qs from 'qs';
import { parseResponse, proxyFetch } from '../fetch';
import { getGoogleCredentials } from '../oauth';

const baseUrl = process.env.REACT_APP_GCP_API;

const projectsNotFoundError = (cause) => new WError({
  name: 'ProjectsNotFound',
  cause,
}, 'projects not found');

export const getProjects = (rackspaceAccountNumber, queryCriteria = {}) => {
  const projectsUrl = `${baseUrl}/projects`;
  const parsedQueryString = qs.stringify(queryCriteria, {
    encode: false,
    allowDots: true,
    arrayFormat: 'comma',
  });

  const projectsGetUrl = parsedQueryString ? `${projectsUrl}?${parsedQueryString}` : projectsUrl;
  const headers = rackspaceAccountNumber ? ({ 'X-Tenant-Id': rackspaceAccountNumber }) : {};

  return proxyFetch(projectsGetUrl, { headers })
    .then(parseResponse)
    .catch((error) => Promise.reject(
      VError.info(error).status === 404 ? projectsNotFoundError(error) : error,
    ));
};

export const updateServiceLevel = ({ productAccount, selectedServices = [] }) => {
  const url = `${baseUrl}/projects/${productAccount.projectId}/serviceLevel`;
  const serviceLevel = _.head(selectedServices);
  return proxyFetch(url, {
    method: 'PUT',
    headers: {
      'X-Tenant-Id': productAccount.rackspaceAccountNumber,
    },
    body: {
      serviceLevel,
    },
  })
    .then(parseResponse);
};

export const updateServiceBlocks = ({ productAccount, selectedServices = [] }) => {
  const url = `${baseUrl}/projects/${productAccount.projectId}/serviceBlocks`;
  return proxyFetch(url, {
    method: 'PUT',
    headers: {
      'X-Tenant-Id': productAccount.rackspaceAccountNumber,
    },
    body: {
      serviceBlocks: _.map(selectedServices, (serviceBlock) => ({ serviceBlockId: serviceBlock })),
    },
  })
    .then(parseResponse);
};

/**
 *
 * @param {string} organizationDomain
 * @returns{Object} Successfully claimed Organization
 * We will show specific errors on gcp/projects list page to user to make onboarding smoother
 * ToDo: 1. Update comment section on Errors:
 *            a. Errors to be shown on Projectlist View Page
 *            b. Errors to be shown on Onboarding page
 *        2. Handle errors during Claiming Organization
 */

export const claimOrganization = (reqPayload) => {
  const url = `${baseUrl}/organizations`;
  const creationTime = _.get(reqPayload, 'googleOrganization.createTime', '');

  const gcpDisplayName = _.get(reqPayload, 'googleOrganization.displayName', '');
  const organizationId = _.get(reqPayload, 'googleOrganization.name').split('/')[1];
  const gcpCreationTime = moment(creationTime).format('YYYY-MM-DD hh:mm:ss');
  const gcpDirectoryCustomerId = _.get(reqPayload, 'googleOrganization.directoryCustomerId', '');
  const gcpLifecycleState = _.get(reqPayload, 'googleOrganization.state', '');
  const googleCredentials = getGoogleCredentials();

  const payload = {
    googleCredentials,
    organizationId,
    gcpDisplayName,
    gcpCreationTime,
    gcpDirectoryCustomerId,
    gcpLifecycleState,
  };

  return proxyFetch(url, {
    method: 'POST',
    body: JSON.stringify(payload),
  })
    .then(parseResponse)
    .catch((error) => Promise.reject(
      VError.info(error).status === 404 ? projectsNotFoundError(error) : error, // yet to handle errors
    ));
};

export const getOrganizations = (rackspaceAccountNumber, queryCriteria = {}) => {
  const organizationsUrl = `${baseUrl}/organizations`;
  const parsedQueryString = qs.stringify(queryCriteria, {
    encode: false,
    allowDots: true,
    arrayFormat: 'comma',
  });

  const organizationsGetUrl = parsedQueryString ? `${organizationsUrl}?${parsedQueryString}` : organizationsUrl;
  const headers = rackspaceAccountNumber ? ({ 'X-Tenant-Id': rackspaceAccountNumber }) : {};

  return proxyFetch(organizationsGetUrl, { headers })
    .then(parseResponse);
};

export const testOrganizationClaims = (organizationIds) => {
  const url = `${baseUrl}/organizations?testOrganizationClaims=${organizationIds}`;
  return proxyFetch(url)
    .then(parseResponse);
};

export const postProjects = (projects) => {
  const googleCredentials = getGoogleCredentials();
  const url = `${baseUrl}/projects`;
  return proxyFetch(url, {
    method: 'POST',
    body: JSON.stringify({
      projects,
      googleCredentials,
    }),
  })
    .then(parseResponse);
};

export const getAccessGrants = () => {
  const url = `${baseUrl}/accessGrants`;
  return proxyFetch(url, {
    method: 'GET',
  })
    .then(parseResponse);
};
